// HTTP transport for the generated operations. Errors are RFC 9457 problems thrown as `ApiError`.
import { bindOperations, operations } from "./operations.gen";
import type { Caller, OperationInput, OperationOutput, Problem, RouteId } from "./types";

export class ApiError extends Error {
  readonly status: number;
  /** The problem body, when the server sent `application/problem+json` (or JSON). */
  readonly problem: Problem | null;
  readonly operation: RouteId;

  constructor(operation: RouteId, status: number, problem: Problem | null) {
    super(problem ? `${problem.code}: ${problem.detail ?? problem.title}` : `HTTP ${status}`);
    this.name = "ApiError";
    this.operation = operation;
    this.status = status;
    this.problem = problem;
  }

  get code(): string | null {
    return this.problem?.code ?? null;
  }
}

export type ClientOptions = {
  /** Origin of the API, e.g. `https://api.example.com` (paths already start with `/v1`). */
  baseUrl: string;
  fetch?: typeof fetch;
  /** Extra headers per request (e.g. `Authorization: Bearer cosk_in_…` for the ingest key). */
  headers?: () => Record<string, string> | Promise<Record<string, string>>;
  /** `include` for the browser session cookie. */
  credentials?: RequestCredentials;
};

/** A fresh `Idempotency-Key` (04 §Idempotency keys). */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

type AnyInput = {
  params?: Record<string, unknown>;
  query?: Record<string, unknown>;
  body?: unknown;
  idempotencyKey?: string;
  ifMatch?: string | number;
  signal?: AbortSignal;
};

export function buildUrl(baseUrl: string, path: string, input: AnyInput): string {
  const filled = path.replace(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, (_, name: string) => {
    const value = input.params?.[name];
    if (value === undefined || value === null) throw new Error(`missing path parameter ${name}`);
    return encodeURIComponent(String(value));
  });
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(input.query ?? {})) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) for (const v of value) search.append(key, String(v));
    else search.append(key, String(value));
  }
  const qs = search.toString();
  return `${baseUrl.replace(/\/$/, "")}${filled}${qs ? `?${qs}` : ""}`;
}

export function createCaller(options: ClientOptions): Caller {
  const doFetch = options.fetch ?? fetch;
  return (async <I extends RouteId>(id: I, rawInput: OperationInput<I>) => {
    const op = operations[id];
    const input = (rawInput ?? {}) as AnyInput;
    const headers: Record<string, string> = {
      accept: "application/json, application/problem+json",
      ...(options.headers ? await options.headers() : {}),
    };
    if (input.idempotencyKey) headers["idempotency-key"] = input.idempotencyKey;
    if (input.ifMatch !== undefined) headers["if-match"] = `"${input.ifMatch}"`;
    let body: BodyInit | undefined;
    if (input.body instanceof FormData) body = input.body;
    else if (input.body !== undefined) {
      headers["content-type"] = "application/json";
      body = JSON.stringify(input.body);
    }
    const init: RequestInit = { method: op.method, headers };
    if (body !== undefined) init.body = body;
    if (options.credentials) init.credentials = options.credentials;
    if (input.signal) init.signal = input.signal;
    const res = await doFetch(buildUrl(options.baseUrl, op.path, input), init);
    if (!res.ok) {
      const type = res.headers.get("content-type") ?? "";
      const problem = type.includes("json") ? ((await res.json()) as Problem) : null;
      throw new ApiError(id, res.status, problem);
    }
    if (res.status === 204 || op.response === "none") return undefined;
    if (op.response === "text") return await res.text();
    if (op.response === "binary") return await res.blob();
    if (op.response === "stream") return res.body;
    return await res.json();
  }) as Caller;
}

export type CompanyOsClient = ReturnType<typeof bindOperations> & {
  /** Untyped-by-group escape hatch: call any operation by id. */
  call: Caller;
};

export function createClient(options: ClientOptions): CompanyOsClient {
  const call = createCaller(options);
  return { ...bindOperations(call), call };
}

export type { OperationInput, OperationOutput };
